/**
 * Алгоритм расчёта нумерологической матрицы судьбы (квадрат Пифагора)
 * Детальные расшифровки по классической нумерологии
 */
import { NUMEROLOGY_TEXTS, getLocale } from './numerologyTexts';

export interface DigitInterpretation {
  number: number;
  count: number;
  shortDescription: string;
  fullDescription: string;
  byCount: string; // интерпретация в зависимости от количества цифр
}

export interface MatrixDetailsResult {
  destinyNumber: number;
  destinyText: string;
  characterLine: string;      // линия 1-4-7: характер, воля
  energyLine: string;         // линия 2-5-8: энергия, семья
  mindLine: string;           // линия 3-6-9: ум, творчество
  temperament: string;        // диагональ 3-5-7
  purpose: string;            // диагональ 1-5-9
  strongNumbers: number[];
  strongNumbersText: string;
  missingNumbers: number[];
  karmaText: string;
  digitInterpretations: DigitInterpretation[];
  character: string;
  energy: string;
  potential: string;
  karma: string;
  lifePath: string;
}

export interface MatrixResult {
  matrix: number[][];
  cellData: Map<number, { number: number; count: number; description: string; fullText: string }>;
  details: MatrixDetailsResult;
}

const nameMap: { [key: string]: number } = {
  'а': 1, 'и': 1, 'с': 1, 'ъ': 1,
  'б': 2, 'й': 2, 'т': 2, 'ы': 2,
  'в': 3, 'к': 3, 'у': 3, 'ь': 3,
  'г': 4, 'л': 4, 'ф': 4, 'э': 4,
  'д': 5, 'м': 5, 'х': 5, 'ю': 5,
  'е': 6, 'н': 6, 'ц': 6, 'я': 6,
  'ё': 6,
  'ж': 7, 'о': 7, 'ч': 7,
  'з': 8, 'п': 8, 'ш': 8,
  'р': 9, 'щ': 9,
};

const nameToNumbers = (name: string): number[] => {
  const lowerName = name.toLowerCase().replace(/\s+/g, '');
  return lowerName.split('').map(char => nameMap[char] || 0).filter(n => n > 0);
};

const dateToNumbers = (dateString: string): number[] => {
  const date = new Date(dateString);
  return [date.getDate(), date.getMonth() + 1, date.getFullYear()];
};

const reduceToSingleDigit = (num: number): number => {
  while (num > 9 && num !== 11 && num !== 22 && num !== 33) {
    num = num.toString().split('').reduce((sum, digit) => sum + parseInt(digit, 10), 0);
  }
  return num;
};

const createMatrix = (numbers: number[]): number[][] => {
  const counts: { [key: number]: number } = {};
  numbers.forEach(num => {
    const reduced = reduceToSingleDigit(num);
    if (reduced >= 1 && reduced <= 9) counts[reduced] = (counts[reduced] || 0) + 1;
  });
  const matrix: number[][] = [[], [], []];
  for (let i = 1; i <= 9; i++) {
    const row = Math.floor((i - 1) / 3);
    const col = (i - 1) % 3;
    matrix[row][col] = counts[i] || 0;
  }
  return matrix;
};

const getLineInterpretation = (
  counts: Record<number, number>,
  indices: [number, number, number],
  lineName: string,
  descriptions: [string, string, string],
  lineTemplates: { weak: string; partial: string; full: string }
): string => {
  const sum = indices.reduce((s, i) => s + (counts[i] || 0), 0);
  const [a, b, c] = indices.map(i => counts[i] || 0);
  if (sum === 0) return `${lineName}: ${lineTemplates.weak}. ${descriptions[0]}`;
  if (a >= 1 && b >= 1 && c >= 1) return `${lineName}: ${lineTemplates.full}. ${descriptions[2]}`;
  return `${lineName}: ${lineTemplates.partial}. ${descriptions[1]}`;
};

export const calculateMatrix = (name: string, birthDate: string, locale?: string): MatrixResult => {
  const loc = getLocale(locale || 'ru');
  const T = NUMEROLOGY_TEXTS[loc];

  const nameNumbers = nameToNumbers(name);
  const dateNumbers = dateToNumbers(birthDate);
  const allNumbers = [...nameNumbers, ...dateNumbers];

  const counts: { [key: number]: number } = {};
  allNumbers.forEach(num => {
    const reduced = reduceToSingleDigit(num);
    if (reduced >= 1 && reduced <= 9) counts[reduced] = (counts[reduced] || 0) + 1;
  });

  const matrix = createMatrix(allNumbers);
  const cellData = new Map<number, { number: number; count: number; description: string; fullText: string }>();

  const digitInterpretations: DigitInterpretation[] = [];
  for (let i = 1; i <= 9; i++) {
    const count = counts[i] || 0;
    const meta = T.digitMeanings[i];
    const byCountKey = count >= 3 ? 3 : count;
    const byCount = T.interpretationByCount[i]?.[byCountKey] ?? '';
    cellData.set(i, {
      number: i,
      count,
      description: meta.short,
      fullText: `${meta.full} ${byCount}`.trim(),
    });
    digitInterpretations.push({
      number: i,
      count,
      shortDescription: meta.short,
      fullDescription: meta.full,
      byCount,
    });
  }

  const destinyNum = reduceToSingleDigit(
    dateNumbers.reduce((s, n) => s + reduceToSingleDigit(n), 0)
  );
  const strongNumbers = (Object.keys(counts) as unknown as number[]).filter(d => (counts[d] || 0) >= 2);
  const missingNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9].filter(d => (counts[d] || 0) === 0);

  const characterLine = getLineInterpretation(
    counts, [1, 4, 7],
    T.lineCharacter.name,
    [T.lineCharacter.weak, T.lineCharacter.partial, T.lineCharacter.full],
    T.lineTemplates
  );
  const energyLine = getLineInterpretation(
    counts, [2, 5, 8],
    T.lineEnergy.name,
    [T.lineEnergy.weak, T.lineEnergy.partial, T.lineEnergy.full],
    T.lineTemplates
  );
  const mindLine = getLineInterpretation(
    counts, [3, 6, 9],
    T.lineMind.name,
    [T.lineMind.weak, T.lineMind.partial, T.lineMind.full],
    T.lineTemplates
  );

  const t357 = (counts[3] || 0) + (counts[5] || 0) + (counts[7] || 0);
  const temperament = T.temperament.prefix + (t357 >= 3 ? T.temperament.high : t357 >= 1 ? T.temperament.mid : T.temperament.low);

  const p159 = (counts[1] || 0) + (counts[5] || 0) + (counts[9] || 0);
  const purpose = T.purpose.prefix + (p159 >= 3 ? T.purpose.high : p159 >= 1 ? T.purpose.mid : T.purpose.low);

  const strongNumbersText = strongNumbers.length > 0
    ? T.strongNumbers.has.replace('{{nums}}', strongNumbers.join(', '))
    : T.strongNumbers.none;

  const karmaText = missingNumbers.length > 0
    ? T.karma.has.replace('{{nums}}', missingNumbers.join(', '))
    : T.karma.none;

  const character = characterLine;
  const energy = energyLine;
  const potential = purpose;
  const karma = karmaText;
  const lifePath = T.destinyTexts[destinyNum] || T.lifePathDefault;

  const details: MatrixDetailsResult = {
    destinyNumber: destinyNum,
    destinyText: T.destinyTexts[destinyNum] || '',
    characterLine,
    energyLine,
    mindLine,
    temperament,
    purpose,
    strongNumbers,
    strongNumbersText,
    missingNumbers,
    karmaText,
    digitInterpretations,
    character,
    energy,
    potential,
    karma,
    lifePath,
  };

  return { matrix, cellData, details };
};
